// Shared by the isolated Plix adapter and map UI. Never infer a recording's
// location from its assigned site, address, or another recording.
export type Coordinate = [number, number];
export function coordinates(lat: unknown, lng: unknown): Coordinate | null {
  if (lat === null || lng === null || lat === '' || lng === '' || lat === undefined || lng === undefined) return null;
  if (!['number', 'string'].includes(typeof lat) || !['number', 'string'].includes(typeof lng)) return null;
  const y = Number(lat), x = Number(lng);
  return Number.isFinite(y) && Number.isFinite(x) && Math.abs(y) <= 90 && Math.abs(x) <= 180 && !(x === 0 && y === 0) ? [x, y] : null;
}
export function timestamp(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Date.parse(String(value));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}
export function safeMediaUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && url.hostname.endsWith('.blob.core.windows.net') ? url.href : undefined;
  } catch { return undefined; }
}
export function mediaExpiry(value: unknown): number | undefined {
  const url = safeMediaUrl(value);
  return url ? timestamp(new URL(url).searchParams.get('se')) : undefined;
}
export function recordingMediaUrl(raw: Record<string, unknown>): string | undefined {
  const sas = raw.BlobSAS ?? raw.sas;
  const direct = safeMediaUrl(sas);
  if (direct) return direct;
  const path = safeMediaUrl(raw.BlobPath ?? raw.bp);
  if (!path || typeof sas !== 'string' || !sas) return undefined;
  const url = new URL(path);
  url.search = sas.replace(/^\?/, '');
  return url.searchParams.has('sig') ? url.href : undefined;
}
export function normalizeRecording(raw: Record<string, unknown>, allowedDevices: Set<string>) {
  const externalId = String(raw.id ?? raw.i ?? '');
  const deviceId = String(raw.deviceId ?? raw.d ?? '');
  const recordedAt = timestamp(raw.TimeRecorded ?? raw.t) ?? 0; // 0 sorts unknown dates last; UI labels them unknown.
  if (!externalId || !allowedDevices.has(deviceId)) return null;
  const point = coordinates(raw.latitude ?? raw.lat, raw.longitude ?? raw.lng);
  const playbackUrl = recordingMediaUrl(raw);
  const duration = Number(raw.Duration ?? raw.dur ?? 0);
  return {
    externalId, deviceId, recordedAt,
    durationMs: Number.isFinite(duration) && duration >= 0 ? duration : 0,
    title: String(raw.AITitle ?? raw.ai ?? '').slice(0, 2000),
    guardName: String(raw.assignedUserName ?? raw.un ?? '').slice(0, 200),
    address: String(raw.address ?? raw.a ?? '').slice(0, 1000),
    ...(point ? { longitude: point[0], latitude: point[1] } : {}),
    ...(timestamp(raw.locationTime) ? { locationTime: timestamp(raw.locationTime) } : {}),
    fileName: String(raw.FileName ?? raw.fn ?? '').slice(0, 1000),
    ...(playbackUrl ? { playbackUrl, ...(mediaExpiry(playbackUrl) ? { expiresAt: mediaExpiry(playbackUrl) } : {}) } : {}),
    status: String(raw.Status ?? raw.st ?? 'unknown'),
  };
}
export function pointInRing(point: Coordinate, ring: Coordinate[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j], b = ring[i];
    const cross = (point[0] - a[0]) * (b[1] - a[1]) - (point[1] - a[1]) * (b[0] - a[0]);
    if (Math.abs(cross) < 1e-12 && point[0] >= Math.min(a[0], b[0]) && point[0] <= Math.max(a[0], b[0]) && point[1] >= Math.min(a[1], b[1]) && point[1] <= Math.max(a[1], b[1])) return true;
    if ((a[1] > point[1]) !== (b[1] > point[1]) && point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}
export function fencePolygons(raw: unknown): Coordinate[][][] {
  try {
    let value: any = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (value?.type === 'Feature') value = value.geometry;
    const multi = value?.type === 'MultiPolygon';
    value = value?.coordinates ?? value;
    if (!Array.isArray(value)) return [];
    if (typeof value[0]?.[0] === 'number') value = [value];
    const polygons = multi ? value : [value];
    return polygons.map((polygon: any) => polygon.map((ring: any) => ring.map((p: any) => {
      if (!Array.isArray(p)) throw new Error('Invalid fence');
      const swap = Math.abs(p[0]) <= 90 && Math.abs(p[1]) > 90;
      const c = coordinates(swap ? p[0] : p[1], swap ? p[1] : p[0]);
      if (!c) throw new Error('Invalid fence');
      return c;
    }))).filter((p: Coordinate[][]) => p[0]?.length >= 3);
  } catch { return []; }
}
export function inFences(point: Coordinate, polygons: Coordinate[][][]): boolean {
  return polygons.some(p => pointInRing(point, p[0]) && !p.slice(1).some(hole => pointInRing(point, hole)));
}
export type TrailPoint = { longitude: number; latitude: number; time: number };
export function normalizeTrail(raw: unknown, start: number, end: number): TrailPoint[] {
  if (!Array.isArray(raw)) return [];
  const byTime = new Map<number, TrailPoint>();
  for (const row of raw) {
    const c = coordinates(row?.latitude, row?.longitude), time = timestamp(row?.time);
    if (c && time && time >= start && time <= end) byTime.set(time, { longitude: c[0], latitude: c[1], time });
  }
  return [...byTime.values()].sort((a, b) => a.time - b.time).slice(0, 20000);
}
// Break missing telemetry intervals rather than drawing an invented route.
export function trailSegments(points: TrailPoint[]): Coordinate[][] {
  const segments: Coordinate[][] = [];
  let current: Coordinate[] = [];
  for (let i = 0; i < points.length; i++) {
    if (i && points[i].time - points[i - 1].time > 120000) { if (current.length > 1) segments.push(current); current = []; }
    current.push([points[i].longitude, points[i].latitude]);
  }
  if (current.length > 1) segments.push(current);
  return segments;
}
